import {
  Cartesian3,
  PrimitiveCollection,
  type Viewer,
} from 'cesium';
import { config } from '../app/config';
import { buildStations } from './stations';
import { buildTrackStructure } from './trackBuilder';
import type { RailFeature } from './types';

interface LiveFeature {
  collection: PrimitiveCollection;
  lastSeen: number;
}

/**
 * Distance-based near-LOD manager. The far layer (railLayer) is always present;
 * this manager generates full 3D track structure only for features within the
 * `nearBuild` radius of the camera, disposes them past `nearDispose`
 * (hysteresis), caps live features (LRU), and builds at most `tilesPerFrame`
 * features per tick to keep the frame smooth.
 *
 * NOTE: geometry is currently built on the main thread. The next step (M6) is to
 * move buildStations' frame/vertex math into a Web Worker, sampling terrain on
 * the main thread first and transferring typed arrays back.
 */
export class NearTrackManager {
  private readonly live = new Map<string, LiveFeature>();
  private readonly midpoints = new Map<string, Cartesian3>();
  private building = false;
  private visible = true;
  private removeListener?: () => void;

  constructor(
    private readonly viewer: Viewer,
    private readonly features: RailFeature[],
  ) {
    for (const f of features) {
      const mid = featureMidpoint(f);
      if (mid) this.midpoints.set(String(f.id), mid);
    }
  }

  start(): void {
    const onChange = () => void this.update();
    this.viewer.camera.changed.addEventListener(onChange);
    this.viewer.camera.percentageChanged = 0.1;
    this.removeListener = () =>
      this.viewer.camera.changed.removeEventListener(onChange);
    void this.update();
  }

  stop(): void {
    this.removeListener?.();
    for (const { collection } of this.live.values()) {
      this.viewer.scene.primitives.remove(collection);
    }
    this.live.clear();
  }

  /** Show/hide all live near-LOD structure and pause building while hidden. */
  setVisible(visible: boolean): void {
    if (this.visible === visible) return;
    this.visible = visible;
    for (const { collection } of this.live.values()) {
      collection.show = visible;
    }
    if (visible) void this.update();
  }

  private async update(): Promise<void> {
    if (this.building || !this.visible) return;
    this.building = true;
    try {
      const cam = this.cameraGround();
      const now = performance.now();

      // Dispose features beyond the dispose radius.
      for (const [id, entry] of this.live) {
        const mid = this.midpoints.get(id);
        if (!mid || Cartesian3.distance(mid, cam) > config.lod.nearDispose) {
          this.viewer.scene.primitives.remove(entry.collection);
          this.live.delete(id);
        }
      }

      // Find candidates to build, nearest first.
      const candidates = this.features
        .filter((f) => !this.live.has(String(f.id)))
        .map((f) => ({ f, d: this.distanceTo(f, cam) }))
        .filter((c) => c.d <= config.lod.nearBuild)
        .sort((a, b) => a.d - b.d)
        .slice(0, config.lod.tilesPerFrame);

      for (const { f } of candidates) {
        const stations = await buildStations(f.line, this.viewer.terrainProvider);
        const collection = buildTrackStructure(stations, f.attributes);
        this.viewer.scene.primitives.add(collection);
        this.live.set(String(f.id), { collection, lastSeen: now });
      }

      // LRU eviction if over budget.
      if (this.live.size > config.lod.maxNearTiles) {
        const sorted = [...this.live.entries()].sort(
          (a, b) => a[1].lastSeen - b[1].lastSeen,
        );
        for (const [id, entry] of sorted.slice(0, this.live.size - config.lod.maxNearTiles)) {
          this.viewer.scene.primitives.remove(entry.collection);
          this.live.delete(id);
        }
      }
    } finally {
      this.building = false;
    }
  }

  private cameraGround(): Cartesian3 {
    const carto = this.viewer.camera.positionCartographic;
    return Cartesian3.fromRadians(carto.longitude, carto.latitude, 0);
  }

  private distanceTo(f: RailFeature, cam: Cartesian3): number {
    const mid = this.midpoints.get(String(f.id));
    return mid ? Cartesian3.distance(mid, cam) : Number.POSITIVE_INFINITY;
  }
}

function featureMidpoint(f: RailFeature): Cartesian3 | undefined {
  if (f.line.length === 0) return undefined;
  const [lon, lat] = f.line[Math.floor(f.line.length / 2)];
  return Cartesian3.fromDegrees(lon, lat, 0);
}
